import React from 'react';

import { ROLE_OPTIONS } from '@vlabs/api-bindings/src/constants';
import {
  BookIcon, ChartBarVariantIcon, ClockIcon, DetectionIcon, DollarSignIcon,
  FileIcon, FileTextIcon, ListIcon, LocationCrosshairIcon, LocationHomeIcon,
  LocationMapIcon, NotificationOnIcon, PeopleIcon, SlidersIcon, TechServerIcon,
  UserCheckIcon, UsersIcon, VideoOnIcon, ViewGridIcon, WeatherThermometerIcon,
} from '@vlabs/icons';
import {
  defaultLanguageOptions, LanguageButton, NavbarItem, NavbarPanel, NavbarPanelFooter, NavbarPanelHeader,
  NavbarPanelSection, NavbarPanelViewer, NavbarTypeToggleButton, ThemeButton, ViewerAuthButton,
} from '@vlabs/monokit/shared/ui/navbar-panel';
import { WhiteLabelBrand } from '@vlabs/monokit/shared/ui/white-label-brand';
import { NavbarLinkWithPermissions } from '@vlabs/shared/components/navbar-link-with-permissions/NavbarLinkWithPermissions';
import { permissions } from '@vlabs/shared/config';
import { routes } from '@vlabs/shared/navigation/routes';
import { selectAppServiceState, selectIsAppFeatureAvailable } from '@vlabs/shared/selectors/appSelectors';
import { useDispatch, useSelector } from 'react-redux';

import { selectAccountModel, selectAccountRole, selectIsAuthenticated } from '@vlabs/pages/auth/selectors';
import { logout } from '@vlabs/pages/auth/store';

import { useNavigationTranslation } from './i18n';

// Оставляем в переключателе только локали, включённые в текущую сборку.
const languageOptions = [
  ...(__LOCALE_RU__ ? defaultLanguageOptions.filter((option) => option.value === 'ru') : []),
  ...(__LOCALE_EN__ ? defaultLanguageOptions.filter((option) => option.value === 'en') : []),
  ...(__LOCALE_PTBR__ ? defaultLanguageOptions.filter((option) => option.value === 'pt-BR') : []),
  ...(__LOCALE_ESES__ ? defaultLanguageOptions.filter((option) => option.value === 'es') : []),
];

function ClementineNavbar(): React.JSX.Element {
  const { t } = useNavigationTranslation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const account = useSelector(selectAccountModel);
  const role = useSelector(selectAccountRole) ?? ROLE_OPTIONS[0];
  const lunaPlatformIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaPlatform')) === 'loaded';
  const thermoIsAvailable = useSelector((state) => selectAppServiceState(state, 'thermo')) === 'loaded';
  const lunaStreamsIsAvailable = useSelector((state) => selectAppServiceState(state, 'lunaStreams')) === 'loaded';
  const vlAccessIsAvailable = useSelector((state) => selectAppServiceState(state, 'vlAccess')) === 'loaded';
  const clementineAuthIsAvailable = useSelector((state) => selectAppServiceState(state, 'clementineAuth')) === 'loaded';
  const vaReporterIsAvailable = useSelector((state) => selectAppServiceState(state, 'vaReporter')) === 'loaded';
  const clementineDepartmentsIsAvailable = useSelector((state) => selectAppServiceState(state, 'clementineDepartments')) === 'loaded';
  const notifierIsAvailable = useSelector((state) => selectAppServiceState(state, 'vlNotifier')) === 'loaded';
  const isFaceQualityAvailable = useSelector((state) => selectIsAppFeatureAvailable(state, 'estimations.face_quality'));
  const videoAnalyticsIsAvailable = useSelector((state) => selectIsAppFeatureAvailable(state, 'components.video_analytics'));

  return (
    <NavbarPanel
      footer={(
        <NavbarPanelFooter
          viewer={(
            <NavbarPanelViewer
              name={account?.name}
              role={ROLE_OPTIONS[role]?.label}
            />
          )}
        >
          <ThemeButton />
          <LanguageButton languageOptions={languageOptions} />
          <ViewerAuthButton
            isAuthenticated={isAuthenticated}
            onLogout={() => {
              logout(dispatch);
            }}
          />
        </NavbarPanelFooter>
      )}
      header={(
        <NavbarPanelHeader
          logo={(<WhiteLabelBrand />)}
          toggleButton={<NavbarTypeToggleButton />}
        />
      )}
      sections={[
        <NavbarPanelSection
          key="events_and_video"
          name={t('events_and_video')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="latest_events"
              icon={<ClockIcon />}
              name={t('latest_events')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.latest}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="event_archive"
              icon={<ListIcon />}
              name={t('event_archive')}
              permissions={{ rules: [permissions.event.view] }}
              to={routes.events.archive}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="search"
              icon={<LocationCrosshairIcon />}
              name={t('search')}
              permissions={{ rules: [permissions.event.matching, permissions.face.matching] }}
              to={routes.search}
            />
          )}
          {!!lunaPlatformIsAvailable && !!isFaceQualityAvailable && (
            <NavbarLinkWithPermissions
              data-testid="checks"
              icon={<DetectionIcon />}
              name={t('checks')}
              permissions={{
                rules: [permissions.resources.iso, permissions.resources.sdk, permissions.emit_events.allowed],
              }}
              to={routes.checks.read}
            />
          )}
          {!!videoAnalyticsIsAvailable && (
            <NavbarItem
              data-testid="queues"
              icon={<PeopleIcon />}
              name={t('queues')}
              to={routes.queue.all}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarItem
              data-testid="event_statistic"
              icon={<ChartBarVariantIcon />}
              name={t('event_statistic')}
              to={routes.events.statistic.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="settings_and_catalogs"
          name={t('settings_and_catalogs')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="faces"
              icon={<UsersIcon />}
              name={t('faces')}
              permissions={{ rules: [permissions.face.view] }}
              to={routes.faces.all}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="lists"
              icon={<ListIcon />}
              name={t('lists')}
              permissions={{ rules: [permissions.list.view] }}
              to={routes.lists.all}
            />
          )}
          {!!(lunaStreamsIsAvailable || vlAccessIsAvailable) && (
            <NavbarLinkWithPermissions
              data-testid="fs_sources"
              icon={<VideoOnIcon />}
              name={t('fs_sources')}
              permissions={{ rules: [permissions.source.view] }}
              to={routes.sources.main}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="handlers"
              icon={<SlidersIcon />}
              name={t('handlers')}
              permissions={{ rules: [permissions.handler.view] }}
              to={routes.handlers.all}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="administration"
          name={t('administration')}
        >
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="tasks"
              icon={<FileIcon />}
              name={t('tasks')}
              permissions={{ rules: [permissions.task.view] }}
              to={routes.tasks.all}
            />
          )}
          {!!clementineAuthIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="accounts"
              icon={<UsersIcon />}
              name={t('accounts')}
              permissions={{ rules: [permissions.account.view] }}
              to={routes.accounts.all}
            />
          )}
          <NavbarLinkWithPermissions
            data-testid="monitoring"
            icon={<TechServerIcon />}
            name={t('monitoring')}
            permissions={{ rules: [permissions.appInfo.view] }}
            to={routes.app.info}
          />
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="features"
              icon={<DollarSignIcon />}
              name={t('features')}
              permissions={{ rules: [permissions.appInfo.view] }}
              to={routes.app.features}
            />
          )}
        </NavbarPanelSection>,
        <NavbarPanelSection
          key="other"
          name={t('other')}
        >
          {!!notifierIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="notifications"
              icon={<NotificationOnIcon />}
              name={t('notifications')}
              permissions={{ rules: [permissions.notification.view] }}
              to={routes.notifications.all}
            />
          )}
          {!!vaReporterIsAvailable && (
            <NavbarItem
              data-testid="vareporter_plans"
              icon={<LocationMapIcon />}
              name={t('vareporter_plans')}
              to={routes.vaReporter.plans.listAll}
            />
          )}
          {!!vaReporterIsAvailable && (
            <NavbarItem
              data-testid="vareporter_reports"
              icon={<FileTextIcon />}
              name={t('vareporter_reports')}
              to={routes.vaReporter.reports}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="verifiers"
              icon={<UserCheckIcon />}
              name={t('verifiers')}
              permissions={{ rules: [permissions.verifier.view] }}
              to={routes.verifiers.all}
            />
          )}
          {!!clementineAuthIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="search_by_documents"
              icon={<BookIcon />}
              name={t('search_by_documents')}
              permissions={{ rules: [permissions.event.view, permissions.face.view] }}
              to={routes.searchByDocuments}
            />
          )}
          {!!clementineDepartmentsIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="departments"
              icon={<LocationHomeIcon />}
              name={t('departments')}
              permissions={{ rules: [permissions.department.view] }}
              to={routes.departments.all}
            />
          )}
          {!!thermoIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="thermo"
              icon={<WeatherThermometerIcon />}
              name={t('thermo')}
              permissions={{ rules: [permissions.thermo.read] }}
              to={routes.thermo.read}
            />
          )}
          {!!lunaPlatformIsAvailable && (
            <NavbarLinkWithPermissions
              data-testid="plugins"
              icon={<ViewGridIcon />}
              name={t('plugins')}
              permissions={{ rules: [permissions.appInfo.view] }}
              to={routes.app.plugins}
            />
          )}
        </NavbarPanelSection>,
      ]}
    />
  );
}

export { ClementineNavbar };
